/**
 * #5688: stop a one-shot PouchDB replication the moment it reports an error.
 *
 * PouchDB 9 does not stop a `live: false, retry: false` replication when one of
 * its requests fails. It throws away the batches it had queued, emits `error`
 * and removes every listener — but the batch it was already writing carries on,
 * and when that batch finishes PouchDB asks the server for more changes again.
 * The replication keeps running with nobody listening, starting from its
 * read-ahead position, so the documents in the thrown-away batches are skipped,
 * and it moves the saved replication position (the checkpoint) past them. The
 * next replication starts from that checkpoint, finds nothing left, and reports
 * a finished download while documents are missing for good.
 *
 * Cancelling the replication inside its own error handler stops that: PouchDB
 * checks the cancelled flag after writing the in-flight batch and goes no
 * further. PouchDB's two-way `sync()` already cancels itself on an error, so
 * only a one-way `replicate.from` / `replicate.to` needs this.
 *
 * See design-docs/sync-integrity.md, "A Failed One-Shot Replication Keeps Running".
 */

/** the part of a PouchDB replication this module uses */
export interface CancellableReplication {
  cancel(): void;
}

/** cancel a one-shot replication that has just reported an error */
export function stopFailedOneShotReplication(replication: CancellableReplication, logHeader: string): void {
  console.log(`${logHeader} stopping the failed one-shot replication so it cannot run on unseen (#5688)`);
  replication.cancel();
}

/**
 * Register an `error` listener that stops `replication` (see the file header).
 * Returns the same replication, so it can wrap the `replicate.from(...)` call.
 */
export function cancelOneShotReplicationOnError<T extends CancellableReplication & { on(event: 'error', cb: (err: unknown) => void): unknown }>(
  replication: T,
  logHeader: string,
): T {
  replication.on('error', () => stopFailedOneShotReplication(replication, logHeader));
  return replication;
}
